import React from "react";
import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";
import paymentMethods from "utils/static/paymentMethods";
import { dateToFullString, dateToString } from "utils/dateUtils";
import { formatCurrency } from "utils/currency";

const getPaymentMethodName = (paymentMethod) => {
    if (paymentMethod) {
        const pm = paymentMethods.find((x) => x.value === paymentMethod);
        return pm ? pm.label : paymentMethod;
    }
    return paymentMethod;
};

const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

const styles = StyleSheet.create({
    page: {
        paddingTop: 20,
        paddingBottom: 65,
        paddingHorizontal: 10,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },
    header: {
        flexDirection: "row",
        marginVertical: 20,
        justifyContent: "center",
    },
    title: {
        fontSize: 18,
        fontWeight: "bold",
    },
    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "right",
        color: "grey",
    },
    time: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "left",
        color: "grey",
    },
    printedBy: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "center",
        color: "grey",
    },
    footer: {
        position: "absolute",
        bottom: 40,
        left: 10,
        right: 10,
    },
    detail: {
        width: "100%",
    },
    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        marginBottom: 10,
    },
    columnHeader: {
        fontSize: 8,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
    row: {
        flexDirection: "row",
        alignContent: "flex-start",
    },
    cell: {
        fontSize: 8,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
});

function getGrandTotal(receiptsData) {
    return receiptsData.reduce((acc, obj) => acc + obj.amount, 0);
}

function getTotals(receiptsData, groupBy, preprocessor) {
    const processedData = preprocessor ? preprocessor(receiptsData) : receiptsData;

    const totals = processedData.reduce((acc, obj) => {
        const groupField = obj[groupBy] || "Unknown";
        if (acc[groupField]) {
            return { ...acc, [groupField]: acc[groupField] + obj.amount };
        }
        return { ...acc, [groupField]: obj.amount };
    }, {});

    if (totals && Object.keys(totals).length > 0) {
        return Object.keys(totals).map((key) => ({ [groupBy]: key, amount: Number(totals[key]) }));
    }

    return [];
}

function cashierPreprocessor(data) {
    return data.map((item) => ({ ...item, cashier: item.user ? item.user.name : null }));
}

const MyDocument = ({ receiptsData, currentUser, moduleName, currency }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
                <View>
                    <Text style={styles.title} fixed>
                        Receipts - {moduleName}
                    </Text>
                </View>
            </View>

            <View style={styles.divider} fixed />

            <View style={styles.detail}>
                <View style={styles.tableHead} fixed>
                    <View style={{ ...styles.columnHeader, flexBasis: "7%" }}>
                        <Text>No.</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flexBasis: "25%" }}>
                        <Text>Client</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flexBasis: "8%" }}>
                        <Text>Date</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "10%" }}>
                        <Text>Amount</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                        <Text>Payment Method</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                        <Text>Cashier</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, flexBasis: "30%" }}>
                        <Text>Description</Text>
                    </View>
                </View>
                {receiptsData &&
                    Array.isArray(receiptsData) &&
                    receiptsData.map((receipt, index) => {
                        return (
                            <View key={index} style={styles.row}>
                                <View style={{ ...styles.cell, flexBasis: "7%" }}>
                                    <Text>{receipt.id}</Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "25%" }}>
                                    <Text>
                                        {`${receipt.fileCase.client.firstName} ${receipt.fileCase.client.middleName} ${receipt.fileCase.client.lastName}`}
                                    </Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "8%" }}>
                                    <Text>{dateToString(receipt.date, false)}</Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.cell,
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        flexBasis: "10%",
                                    }}
                                >
                                    <Text>
                                        {receipt.amount && Number(receipt.amount) > 0 ? (
                                            <Money amount={receipt.amount} currency={currency} />
                                        ) : (
                                            ""
                                        )}
                                    </Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                    <Text>{getPaymentMethodName(receipt.paymentMethod)}</Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                    <Text>{receipt.user.name}</Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "30%" }}>
                                    <Text>{receipt.description}</Text>
                                </View>
                            </View>
                        );
                    })}
                <View style={{ ...styles.row, height: 30, backgroundColor: "#f6f6f6", alignItems: "center" }}>
                    <View style={{ ...styles.cell, flexBasis: "25%" }}>
                        <Text>GRAND TOTAL</Text>
                    </View>
                    <View style={{ ...styles.cell, flexBasis: "7%" }}></View>
                    <View style={{ ...styles.cell, flexBasis: "8%" }}></View>
                    <View
                        style={{
                            ...styles.cell,
                            textAlign: "right",
                            fontWeight: "bold",
                            flexBasis: "10%",
                        }}
                    >
                        <Text>
                            <Money amount={getGrandTotal(receiptsData)} currency={currency} />
                        </Text>
                    </View>
                    <View style={{ ...styles.cell, flexBasis: "10%" }}></View>
                    <View style={{ ...styles.cell, flexBasis: "10%" }}></View>
                    <View style={{ ...styles.cell, flexBasis: "30%" }}></View>
                </View>
            </View>

            <Text style={{ marginTop: "30px", fontSize: 10 }}>Totals by Payment Method</Text>
            <View style={{ width: "40%", marginTop: "10px", borderWidth: 1, borderColor: "gray" }}>
                <View style={styles.tableHead} fixed>
                    <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                        <Text>Payment Method</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "10%" }}>
                        <Text>Amount</Text>
                    </View>
                </View>
                {receiptsData &&
                    Array.isArray(receiptsData) &&
                    getTotals(receiptsData, "paymentMethod").map((total, index) => (
                        <View key={index} style={styles.row}>
                            <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                <Text>{getPaymentMethodName(total.paymentMethod)}</Text>
                            </View>
                            <View
                                style={{
                                    ...styles.cell,
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    flexBasis: "10%",
                                }}
                            >
                                <Text>
                                    {total.amount && Number(total.amount) > 0 ? <Money amount={total.amount} currency={currency} /> : ""}
                                </Text>
                            </View>
                        </View>
                    ))}
            </View>

            <Text style={{ marginTop: "30px", fontSize: 10 }}>Totals by Cashier</Text>
            <View style={{ width: "40%", marginTop: "10px", borderWidth: 1, borderColor: "gray" }}>
                <View style={styles.tableHead} fixed>
                    <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                        <Text>Cashier</Text>
                    </View>
                    <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "10%" }}>
                        <Text>Amount</Text>
                    </View>
                </View>
                {receiptsData &&
                    Array.isArray(receiptsData) &&
                    getTotals(receiptsData, "cashier", cashierPreprocessor).map((total, index) => (
                        <View key={index} style={styles.row}>
                            <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                <Text>{total.cashier}</Text>
                            </View>
                            <View
                                style={{
                                    ...styles.cell,
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    flexBasis: "10%",
                                }}
                            >
                                <Text>
                                    {total.amount && Number(total.amount) > 0 ? <Money amount={total.amount} currency={currency} /> : ""}
                                </Text>
                            </View>
                        </View>
                    ))}
            </View>

            <View style={styles.footer}>
                <View style={styles.divider} />
            </View>

            <Text style={styles.time} render={() => dateToFullString(new Date())} fixed />

            <Text style={styles.printedBy} render={() => `Printed by ${currentUser.name}`} fixed />

            <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </Page>
    </Document>
);

export default MyDocument;
